/**
 * The two-step focus-mode flow, as a pure function of props: pick the year
 * and title, then confirm. One `Dialog`, two steps. `Dialog` handles the
 * under-768px sheet and the 768px-and-up centered dialog itself, so this
 * file never branches on viewport width.
 */
import { Button, Dialog, Input } from "@sportsmet/ui";
import type { JSX } from "react";

import {
  CANCEL_LABEL,
  CONFIRM_BODY,
  CONFIRM_TITLE,
  PICKER_CONTINUE_LABEL,
  PICKER_TITLE,
  PICKER_TITLE_LABEL,
  SEASON_CARD_START_LABEL,
  SEASON_TITLE_MAX_LENGTH,
  titleCounterLabel,
  validateSeasonTitle,
} from "./seasonCardModel";
import { SeasonYearStepper } from "./SeasonYearStepper";

export type typeStartSeasonDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: "pick" | "confirm";
  year: number;
  minYear: number;
  maxYear: number;
  onYearChange: (next: number) => void;
  title: string;
  onTitleChange: (next: string) => void;
  isSubmitting: boolean;
  inlineNote: string | null;
  onContinue: () => void;
  onBackToPick: () => void;
  onConfirm: () => void;
};

export function StartSeasonDialog(
  props: typeStartSeasonDialogProps,
): JSX.Element {
  const {
    open,
    onOpenChange,
    step,
    year,
    minYear,
    maxYear,
    onYearChange,
    title,
    onTitleChange,
    isSubmitting,
    inlineNote,
    onContinue,
    onBackToPick,
    onConfirm,
  } = props;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={step === "pick" ? PICKER_TITLE : CONFIRM_TITLE}
      autoFocus={step === "pick"}
      isDirty={step === "pick" && title.trim().length > 0}
      onRequestClose={() => {
        onOpenChange(false);
      }}
      footer={
        step === "pick" ? (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              {CANCEL_LABEL}
            </Button>
            <Button
              variant="primary"
              data-testid="season-continue"
              disabled={!validateSeasonTitle(title).isValid}
              onClick={onContinue}
            >
              {PICKER_CONTINUE_LABEL}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onBackToPick}>
              {CANCEL_LABEL}
            </Button>
            <Button
              variant="primary"
              data-testid="season-confirm-start"
              disabled={isSubmitting}
              onClick={onConfirm}
            >
              {SEASON_CARD_START_LABEL}
            </Button>
          </>
        )
      }
    >
      {step === "pick" ? (
        <div className="flex flex-col gap-4">
          <SeasonYearStepper
            value={year}
            minYear={minYear}
            maxYear={maxYear}
            onChange={onYearChange}
          />
          <Input
            label={PICKER_TITLE_LABEL}
            data-testid="season-title-input"
            maxLength={SEASON_TITLE_MAX_LENGTH}
            value={title}
            onChange={(event) => {
              onTitleChange(event.target.value);
            }}
          />
          <span
            data-testid="season-title-counter"
            className="text-caption text-ink-secondary"
          >
            {titleCounterLabel({
              used: title.trim().length,
              max: SEASON_TITLE_MAX_LENGTH,
            })}
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p data-testid="season-confirm-body">{CONFIRM_BODY}</p>
          {inlineNote !== null ? (
            <p data-testid="season-inline-note">{inlineNote}</p>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
